/**
 * FAIR CONTACTS PANEL (TS-100) — Pannello "Fiere" nella colonna destra della
 * scheda contatto: elenca i contatti fiera (RapidContact) importati e collegati
 * a questo Person, mostrando nome fiera + anno (anno del createDate del record).
 * Sola lettura. Se non ci sono contatti fiera collegati, il pannello non compare.
 * Ogni voce è un link alla scheda del contatto fiera (`/rapid-contact/:id`), che per i
 * record importati si apre anch'essa in sola lettura.
 *
 * Riusa lo stile dei RelationPanel (.panel-box / .relation-list / .relation-link) ma senza
 * azioni (add/remove): il link RapidContact→Person è di sistema (impostato dall'import).
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { rapidContactApi } from '../entities/rapidContact/rapidContactApi';
import type { RapidContact } from '../entities/rapidContact/rapidContactTypes';
import { useToast } from './useToast';

interface FairContactsPanelProps {
  personId: number;
}

export function FairContactsPanel({ personId }: FairContactsPanelProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [contacts, setContacts] = useState<RapidContact[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const list = await rapidContactApi.getByPerson(personId);
        if (active) setContacts(list);
      } catch (err) {
        if (active)
          toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
      } finally {
        if (active) setLoaded(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [personId, toast]);

  // Nessun contatto fiera collegato -> pannello non mostrato.
  if (!loaded || contacts.length === 0) return null;

  return (
    <Accordion defaultExpanded disableGutters elevation={0} className="panel-box">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box className="relation-header">
          <Typography className="relation-title">
            {t('rapidContact.fairsPanel.title')} ({contacts.length})
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails className="panel-box__details">
        <ul className="relation-list">
          {contacts.map((c) => (
            <li key={c.rapidContactId} className="relation-list__item">
              {/* Voce cliccabile verso la scheda del contatto fiera, stesso `relation-link`
                  dei RelationPanel. Essendo importati (il pannello mostra solo quelli),
                  la pagina di destinazione si apre in sola lettura.
                  `state.backTo` dice alla scheda dove riportare l'utente: qui il contatto
                  da cui è partito, non la lista Fiere (che è il ripiego). */}
              <Link
                to={`/rapid-contact/${c.rapidContactId}`}
                state={{ backTo: { path: `/contacts/${personId}`, labelKey: 'rapidContact.backToContact' } }}
                className="relation-link"
              >
                {c.fairEvent || t('rapidContact.fairsPanel.unnamed')}
                {c.createDate ? ` · ${new Date(c.createDate).getFullYear()}` : ''}
              </Link>
            </li>
          ))}
        </ul>
      </AccordionDetails>
    </Accordion>
  );
}
